import { useState, useCallback } from 'react';
import { API } from '@/utils/API';
import { WizardSelection } from '@/components/wizard/types';

interface UseFindFormResult {
  /**
   * Resolves the selection to template ids — one per chosen sub-category. The first is
   * the primary template; any others are merged into the same submission (OV-1180).
   */
  findForms: () => Promise<string[] | null>;
  isLoading: boolean;
  error: string | null;
  formIds: string[];
}

const getForm = async (
  category: string,
  subCategory: string,
  variant: string
): Promise<{ id: string }> => {
  const params = new URLSearchParams({
    category,
    sub_category: subCategory,
    variant,
  });

  const response = await API.authenticated.fetch(
    `/submission_templates?${params.toString()}`
  );

  if (!response.ok) {
    throw new Error('Failed to find form');
  }

  return response.json();
};

export const useFindForm = (selection: WizardSelection): UseFindFormResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formIds, setFormIds] = useState<string[]>([]);

  const findForms = useCallback(async (): Promise<string[] | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const { category } = selection;
      if (!category) {
        throw new Error('Category is required');
      }

      // A category with no sub-categories still resolves to one template.
      const subCategories = selection.subCategory?.length
        ? selection.subCategory
        : [''];

      const results = await Promise.all(
        subCategories.map(subCategory =>
          getForm(category, subCategory, selection.variant?.[subCategory] ?? '')
        )
      );

      const ids = results.map(result => {
        if (!result?.id) {
          throw new Error('Invalid response: missing form ID');
        }
        return result.id;
      });

      setFormIds(ids);
      return ids;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to find form';
      console.error('Find form error:', err);
      setError(errorMessage);
      setFormIds([]);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [selection]);

  return {
    findForms,
    isLoading,
    error,
    formIds,
  };
};
